import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronDown, ChevronUp, BookOpen, Brain, Target, Music, Sparkles, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import PageHeader from '../components/PageHeader';

const Guide = ({ t = {} }) => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState('philosophy');

  // Fallback translations in case t is not properly loaded
  const translations = {
    home: t?.home || 'Accueil',
    guideTitle: t?.guideTitle || 'Guide Utilisateur',
    guideSubtitle: t?.guideSubtitle || 'Votre compagnon de rééducation auditive',
    guideImportant: t?.guideImportant || 'Important',
    guideImportantText: t?.guideImportantText || 'HEAREN est une méthode complémentaire de bien-être, non médicale. Elle ne remplace pas les soins médicaux. Consultez toujours un médecin ORL si vous souffrez d\'acouphènes.',
    guideTableOfContents: t?.guideTableOfContents || 'Table des Matières',
    guidePhilosophy: t?.guidePhilosophy || 'Philosophie HEAREN',
    guideHowWorks: t?.guideHowWorks || 'Comment ça marche ?',
    guideStep1Title: t?.guideStep1Title || 'Étape 1: Test Audiométrique',
    guideStep2Title: t?.guideStep2Title || 'Étape 2: Test Acouphènes',
    guideStep3Title: t?.guideStep3Title || 'Étape 3: Écoute Personnalisée',
    guideDailyUsage: t?.guideDailyUsage || 'Utilisation Quotidienne',
    guideWhatToExpect: t?.guideWhatToExpect || 'À Quoi S\'Attendre',
    guideTips: t?.guideTips || 'Conseils et Meilleures Pratiques',
    ...t // Spread all other translations
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
    
    // Scroll to the section after a short delay to allow state update
    setTimeout(() => {
      const element = document.getElementById(`section-${section}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const Section = ({ id, icon: Icon, title, children }) => {
    const isExpanded = expandedSection === id;
    
    return (
      <Card className="mb-4" id={`section-${id}`}>
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection(id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon className="text-purple-600" size={24} />
              <CardTitle className="text-lg">{title}</CardTitle>
            </div>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="space-y-4 pt-4">
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-4 md:px-8 pb-40">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <PageHeader t={translations} />
          
          <div className="text-center mb-8">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-purple-600" />
            <h1 className="text-4xl font-bold text-white mb-2">
              {translations.guideTitle}
            </h1>
            <p className="text-xl text-gray-300">
              {translations.guideSubtitle}
            </p>
          </div>

          {/* Important Notice */}
          <Alert className="bg-slate-800 border-slate-700 mb-6">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <AlertDescription className="text-gray-200">
              <strong>{translations.guideImportant}:</strong> {translations.guideImportantText}
            </AlertDescription>
          </Alert>
        </div>

        {/* Table of Contents */}
        <Card className="mb-8 bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              📋 {translations.guideTableOfContents}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
              <button onClick={() => toggleSection('philosophy')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guidePhilosophy}
              </button>
              <button onClick={() => toggleSection('howworks')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guideHowWorks}
              </button>
              <button onClick={() => toggleSection('step1')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guideStep1Title}
              </button>
              <button onClick={() => toggleSection('step2')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guideStep2Title}
              </button>
              <button onClick={() => toggleSection('step3')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guideStep3Title}
              </button>
              <button onClick={() => toggleSection('daily')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guideDailyUsage}
              </button>
              <button onClick={() => toggleSection('expect')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guideWhatToExpect}
              </button>
              <button onClick={() => toggleSection('tips')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guideTips}
              </button>
              <button onClick={() => toggleSection('audioquality')} className="text-left text-gray-200 hover:text-purple-400 transition-colors">
                • {translations.guideAudioQuality}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        
        {/* Philosophy */}
        <Section id="philosophy" icon={Brain} title={translations.guidePhilosophy}>
          <div className="space-y-4">
            <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
              <p className="text-lg font-semibold text-white mb-2">
                {translations.guidePhilosophyQuote}
              </p>
              <p className="text-gray-200">
                {translations.guidePhilosophyText}
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-white flex items-center gap-2">
                <Target size={20} className="text-purple-600" />
                {translations.guidePhilosophy5Pillars}
              </h4>
              
              {/* Pillar 1 */}
              <div className="p-3 bg-slate-800 rounded border-l-4 border-blue-500">
                <h5 className="font-semibold text-white mb-1">
                  1. 🎯 {translations.guidePillar1Title}
                </h5>
                <p className="text-sm text-gray-200">{translations.guidePillar1Text}</p>
                <div className="mt-2 p-2 bg-slate-700 rounded text-xs text-gray-300">
                  💡 <strong>{translations.guideNotchExplained}:</strong> {translations.guideNotchExplainedText}
                </div>
              </div>

              {/* Pillar 2 */}
              <div className="p-3 bg-slate-800 rounded border-l-4 border-green-500">
                <h5 className="font-semibold text-white mb-1">
                  2. 🧠 {translations.guidePillar2Title}
                </h5>
                <p className="text-sm text-gray-200">{translations.guidePillar2Text}</p>
              </div>

              {/* Pillar 3 */}
              <div className="p-3 bg-slate-800 rounded border-l-4 border-purple-500">
                <h5 className="font-semibold text-white mb-1">
                  3. 🎶 {translations.guidePillar3Title}
                </h5>
                <p className="text-sm text-gray-200">{translations.guidePillar3Text}</p>
              </div>

              {/* Pillar 4 */}
              <div className="p-3 bg-slate-800 rounded border-l-4 border-pink-500">
                <h5 className="font-semibold text-white mb-1">
                  4. 🧘 {translations.guidePillar4Title}
                </h5>
                <p className="text-sm text-gray-200">{translations.guidePillar4Text}</p>
              </div>

              {/* Pillar 5 */}
              <div className="p-3 bg-slate-800 rounded border-l-4 border-yellow-500">
                <h5 className="font-semibold text-white mb-1">
                  5. 🌱 {translations.guidePillar5Title}
                </h5>
                <p className="text-sm text-gray-200">{translations.guidePillar5Text}</p>
              </div>
            </div>
          </div>
        </Section>

        {/* How It Works */}
        <Section id="howworks" icon={Sparkles} title={translations.guideHowWorks}>
          <div className="space-y-4">
            <div className="p-4 bg-slate-800 rounded-lg">
              <h4 className="font-semibold text-white mb-2">{translations.guideScienceSimple}</h4>
              <p className="text-white mb-3">{translations.guideScienceText}</p>
              <p className="text-white">{translations.guideScienceText2}</p>
            </div>

            <div className="p-4 bg-slate-800 rounded-lg">
              <h4 className="font-semibold text-white mb-3 text-center">
                {translations.guide3Phases}
              </h4>
              <div className="space-y-2 text-center">
                <div className="p-3 bg-slate-800 rounded shadow-sm">
                  <span className="font-semibold text-purple-600">Phase 1: {translations.guidePhase1}</span>
                  <p className="text-sm text-gray-300 mt-1">{translations.guidePhase1Sub}</p>
                </div>
                <div className="text-2xl">↓</div>
                <div className="p-3 bg-slate-800 rounded shadow-sm">
                  <span className="font-semibold text-purple-600">Phase 2: {translations.guidePhase2}</span>
                  <p className="text-sm text-gray-300 mt-1">{translations.guidePhase2Sub}</p>
                </div>
                <div className="text-2xl">↓</div>
                <div className="p-3 bg-slate-800 rounded shadow-sm">
                  <span className="font-semibold text-purple-600">Phase 3: {translations.guidePhase3}</span>
                  <p className="text-sm text-gray-300 mt-1">{translations.guidePhase3Sub}</p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Step 1: Audiometric Test */}
        <Section id="step1" icon={Target} title={translations.guideStep1Title}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-slate-800 rounded">
              <Target className="text-blue-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <h4 className="font-semibold text-white">{translations.guideStep1Goal}</h4>
                <p className="text-sm text-white">{translations.guideStep1GoalText}</p>
              </div>
            </div>

            <div className="p-3 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-2">{translations.guideHowItWorks}</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-200">
                <li><strong>{translations.guideStep1Point1}</strong>: {translations.guideStep1Point1Text}</li>
                <li><strong>{translations.guideStep1Point2}</strong>: {translations.guideStep1Point2Text}</li>
                <li><strong>{translations.guideStep1Point3}</strong>: {translations.guideStep1Point3Text}</li>
                <li><strong>{translations.guideStep1Point4}</strong>: {translations.guideStep1Point4Text}</li>
              </ol>
            </div>

            <Alert className="bg-slate-800 border-slate-700">
              <AlertDescription className="text-gray-200">
                <strong>💡 {translations.guideAdvice}:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li>{translations.guideStep1Tip1}</li>
                  <li>{translations.guideStep1Tip2}</li>
                  <li>{translations.guideStep1Tip3}</li>
                  <li>{translations.guideStep1Tip4}</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="p-3 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-1">{translations.guideWhatItProvides}</h4>
              <ul className="list-disc list-inside text-sm text-white space-y-1">
                <li>{translations.guideStep1Provides1}</li>
                <li>{translations.guideStep1Provides2}</li>
                <li>{translations.guideStep1Provides3}</li>
              </ul>
            </div>

            <Button onClick={() => navigate('/audiometric-test')} className="w-full">
              {translations.startTest} →
            </Button>
          </div>
        </Section>

        {/* Step 2: Tinnitus Test */}
        <Section id="step2" icon={Music} title={translations.guideStep2Title}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-slate-800 rounded">
              <Music className="text-purple-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <h4 className="font-semibold text-white">{translations.guideStep2Goal}</h4>
                <p className="text-sm text-white">{translations.guideStep2GoalText}</p>
              </div>
            </div>

            <div className="p-3 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-3">{translations.guideHowItWorks}</h4>
              
              <div className="space-y-3">
                <div className="p-3 bg-slate-700 rounded">
                  <h5 className="font-semibold text-white mb-1">{translations.guidePhase1Title}</h5>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-200">
                    <li>{translations.guideStep2Phase1Point1}</li>
                    <li>{translations.guideStep2Phase1Point2}</li>
                    <li>{translations.guideStep2Phase1Point3}</li>
                    <li>{translations.guideStep2Phase1Point4}</li>
                  </ol>
                </div>

                <div className="p-3 bg-slate-700 rounded">
                  <h5 className="font-semibold text-white mb-1">{translations.guidePhase2Title}</h5>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-200">
                    <li>{translations.guideStep2Phase2Point1}</li>
                    <li>{translations.guideStep2Phase2Point2}</li>
                    <li>{translations.guideStep2Phase2Point3}</li>
                  </ol>
                </div>
              </div>
            </div>

            <Alert className="bg-slate-800 border-slate-700">
              <AlertDescription className="text-gray-200">
                <strong>🔊 {translations.guideVolumeAdjustment}:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li>{translations.guideStep2Volume1}</li>
                  <li><strong>{translations.guideStep2Volume2}</strong></li>
                  <li>{translations.guideStep2Volume3}</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="p-3 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-2">{translations.guideAdaptiveGain || 'Atténuation adaptative'}</h4>
              <div className="text-sm text-white space-y-1">
                <p>• 4 dB SPL → <strong className="text-green-600">-4 dB {translations.guideAttenuation || 'atténuation'}</strong></p>
                <p>• 10 dB SPL → <strong className="text-yellow-600">-10 dB {translations.guideAttenuation || 'atténuation'}</strong></p>
                <p>• 15+ dB SPL → <strong className="text-orange-600">-15 dB {translations.guideAttenuation || 'atténuation'}</strong> (max)</p>
              </div>
            </div>

            <Button onClick={() => navigate('/tinnitus-test')} className="w-full">
              {translations.startTest} →
            </Button>
          </div>
        </Section>

        {/* Step 3: Therapy */}
        <Section id="step3" icon={Sparkles} title={translations.guideStep3Title}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-slate-800 rounded">
              <Sparkles className="text-pink-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <h4 className="font-semibold text-white">{translations.guideStep3Goal}</h4>
                <p className="text-sm text-white">{translations.guideStep3GoalText}</p>
              </div>
            </div>

            {/* Therapeutic Method Explanation */}
            <Card className="bg-white border-2 border-blue-500 shadow-lg">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Brain className="h-6 w-6 text-blue-700" />
                  <p className="font-bold text-blue-700 text-base">{translations.soundMethodTitle || "🎯 Méthode de paysages sonores"}</p>
                </div>
                <p className="font-semibold text-gray-900 italic bg-blue-50 border-l-4 border-blue-600 p-3 rounded text-sm">
                  {translations.therapyPhilosophy || "Il ne s'agit pas d'éteindre le son. Il s'agit d'apprendre à le faire disparaître naturellement."}
                </p>
                <div className="bg-blue-50 p-4 rounded border border-blue-300 space-y-2">
                  <p className="font-semibold text-gray-900 text-sm">{translations.howItWorks || "Comment ça fonctionne:"}</p>
                  <ul className="text-sm space-y-1.5 list-disc list-inside text-gray-900">
                    <li>{translations.therapyPoint1 || "Mesure de votre acouphène (volume en dB SPL)"}</li>
                    <li>{translations.therapyPoint2Updated || "Calcul automatique de l'atténuation de paysages sonores (proportionnelle au volume)"}</li>
                    <li>{translations.therapyPoint3Updated || "Application du Notch classique lors de vos sessions de paysages sonores"}</li>
                  </ul>
                  <p className="text-sm leading-relaxed mt-2 text-gray-900 pt-2 border-t border-blue-300">
                    {translations.therapyPrincipleNotch || "L'atténuation appliquée est proportionnelle au volume de votre acouphène (max -15 dB). Cette méthode Notch classique cible précisément la fréquence de l'acouphène pour réduire sa perception. Le filtre notch atténue le signal sonore à la fréquence problématique, permettant au cerveau de moins se concentrer sur l'acouphène. Cette approche favorise l'habituation naturelle et la réorganisation neuronale."}
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 bg-slate-800 rounded">
                <h4 className="font-semibold text-white mb-2">1. {translations.guideTherapyChoice}</h4>
                <ul className="text-sm text-white space-y-1">
                  <li>• {translations.pinkNoise}</li>
                  <li>• {translations.brownNoise}</li>
                  <li>• {translations.whiteNoise}</li>
                  <li>• {translations.musicalStyles}</li>
                </ul>
                <div className="mt-3 p-2 bg-amber-900/30 border border-amber-700/50 rounded text-xs text-amber-200 italic">
                  💡 {translations.noiseRecommendation || "Nous vous recommandons d'utiliser les bruits blancs, bruns, roses avec modération, leur préférer des sons naturels ou des styles musicaux."}
                </div>
              </div>

              <div className="p-3 bg-slate-800 rounded">
                <h4 className="font-semibold text-white mb-2">2. {translations.personalizedEqualizer}</h4>
                <ul className="text-sm text-white space-y-1">
                  <li>✅ {translations.audiometricCompensation}</li>
                  <li>✅ {translations.tinnitusTherapy}</li>
                </ul>
              </div>
            </div>

            <div className="p-3 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-2">3. {translations.guideSpectrumAnalyzer}</h4>
              <div className="text-sm text-white space-y-1">
                <p>• 🟢 {translations.guideSpectrumGreen}</p>
                <p>• 🟠 {translations.guideSpectrumOrange}</p>
                <p>• 🔴 {translations.guideSpectrumRed}</p>
              </div>
            </div>

            <Button onClick={() => navigate('/therapy')} className="w-full">
              {translations.therapy} →
            </Button>
          </div>
        </Section>

        {/* Daily Usage */}
        <Section id="daily" icon={Clock} title={translations.guideDailyUsage}>
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-3 bg-slate-800 rounded text-center">
                <h4 className="font-semibold text-white mb-1">{translations.guideBeginner}</h4>
                <p className="text-2xl font-bold text-green-600">15-20 min</p>
                <p className="text-xs text-gray-300">{translations.guideBeginnerSessions}</p>
              </div>
              <div className="p-3 bg-slate-800 rounded text-center">
                <h4 className="font-semibold text-white mb-1">{translations.guideRegular}</h4>
                <p className="text-2xl font-bold text-blue-600">30-45 min</p>
                <p className="text-xs text-gray-300">{translations.guideRegularSessions}</p>
              </div>
              <div className="p-3 bg-slate-800 rounded text-center">
                <h4 className="font-semibold text-white mb-1">{translations.guideIntensive}</h4>
                <p className="text-2xl font-bold text-purple-600">45-60 min</p>
                <p className="text-xs text-gray-300">{translations.guideIntensiveSessions}</p>
              </div>
            </div>

            <Alert className="bg-slate-800 border-slate-700">
              <AlertDescription className="text-gray-200">
                <strong>💡 {translations.guideKey}:</strong> {translations.guideRegularityKey}
              </AlertDescription>
            </Alert>

            <div className="p-3 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-2">{translations.guideBestTime}</h4>
              <div className="grid md:grid-cols-2 gap-2 text-sm">
                <div className="p-2 bg-slate-700 rounded text-gray-200">🌅 {translations.guideMorning}</div>
                <div className="p-2 bg-slate-700 rounded text-gray-200">🏢 {translations.guideLunch}</div>
                <div className="p-2 bg-slate-700 rounded text-gray-200">🌆 {translations.guideEvening}</div>
                <div className="p-2 bg-slate-700 rounded text-gray-200">🌙 {translations.guideBedtime}</div>
              </div>
            </div>

            <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
              <h4 className="font-semibold text-white mb-2">{translations.guideGoldenRule}</h4>
              <p className="text-white italic">{translations.guideGoldenRuleText}</p>
            </div>
          </div>
        </Section>

        {/* What to Expect */}
        <Section id="expect" icon={TrendingUp} title={translations.guideWhatToExpect}>
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-slate-800 rounded border-l-4 border-blue-500">
                <h4 className="font-semibold text-white">{translations.guideWeek12}</h4>
                <p className="text-sm text-white">{translations.guideWeek12Text}</p>
              </div>

              <div className="p-3 bg-slate-800 rounded border-l-4 border-green-500">
                <h4 className="font-semibold text-white">{translations.guideWeek34}</h4>
                <p className="text-sm text-white">{translations.guideWeek34Text}</p>
              </div>

              <div className="p-3 bg-slate-800 rounded border-l-4 border-yellow-500">
                <h4 className="font-semibold text-white">{translations.guideMonth23}</h4>
                <p className="text-sm text-white">{translations.guideMonth23Text}</p>
              </div>

              <div className="p-3 bg-slate-800 rounded border-l-4 border-purple-500">
                <h4 className="font-semibold text-white">{translations.guideMonth46}</h4>
                <p className="text-sm text-white">{translations.guideMonth46Text}</p>
              </div>
            </div>

            <Alert className="bg-slate-800 border-slate-700">
              <AlertDescription className="text-gray-200">
                <strong>⚠️ {translations.guideCaution}:</strong> {translations.guideCautionText}
              </AlertDescription>
            </Alert>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 bg-slate-800 rounded">
                <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <CheckCircle size={18} />
                  {translations.guidePositiveSigns}
                </h4>
                <ul className="text-sm text-white space-y-1 list-disc list-inside">
                  <li>{translations.guideSign1}</li>
                  <li>{translations.guideSign2}</li>
                  <li>{translations.guideSign3}</li>
                </ul>
              </div>

              <div className="p-3 bg-slate-800 rounded">
                <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <AlertCircle size={18} />
                  {translations.guideConsultDoctor}
                </h4>
                <ul className="text-sm text-white space-y-1 list-disc list-inside">
                  <li>{translations.guideConsult1}</li>
                  <li>{translations.guideConsult2}</li>
                  <li>{translations.guideConsult3}</li>
                </ul>
              </div>
            </div>
          </div>
        </Section>

        {/* Tips */}
        <Section id="tips" icon={CheckCircle} title={translations.guideTips}>
          <div className="space-y-4">
            <div className="p-4 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <CheckCircle size={20} />
                ✅ {translations.guideDo}
              </h4>
              <ul className="text-sm text-white space-y-1 list-disc list-inside">
                <li>{translations.guideDo1}</li>
                <li>{translations.guideDo2}</li>
                <li>{translations.guideDo3}</li>
                <li>{translations.guideDo4}</li>
                <li>{translations.guideDo5}</li>
              </ul>
            </div>

            <div className="p-4 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <AlertCircle size={20} />
                ❌ {translations.guideDont}
              </h4>
              <ul className="text-sm text-white space-y-1 list-disc list-inside">
                <li>{translations.guideDont1}</li>
                <li>{translations.guideDont2}</li>
                <li>{translations.guideDont3}</li>
                <li>{translations.guideDont4}</li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Audio Quality */}
        <Section id="audioquality" icon={Music} title={translations.guideAudioQuality}>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg border border-purple-500/30">
              <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                <Music size={20} className="text-purple-400" />
                {translations.guideAudioQualityTitle}
              </h4>
              <p className="text-sm text-white/90">{translations.guideAudioQualityText}</p>
            </div>

            <div className="p-4 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-3">📊 {translations.guideAudioStandards}</h4>
              <ul className="text-sm text-white space-y-2 list-none">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>{translations.guideAudioStandard1}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>{translations.guideAudioStandard2}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  <span>{translations.guideAudioStandard3}</span>
                </li>
              </ul>
            </div>

            <div className="p-4 bg-slate-800 rounded">
              <h4 className="font-semibold text-white mb-3">🎧 {translations.guideAudioBenefits}</h4>
              <ul className="text-sm text-white space-y-2 list-disc list-inside">
                <li>{translations.guideAudioBenefit1}</li>
                <li>{translations.guideAudioBenefit2}</li>
                <li>{translations.guideAudioBenefit3}</li>
                <li>{translations.guideAudioBenefit4}</li>
              </ul>
            </div>

            <Alert className="bg-blue-900/30 border-blue-500/50">
              <AlertDescription className="text-gray-200">
                <strong>{translations.guideAudioNote}:</strong> {translations.guideAudioNoteText}
              </AlertDescription>
            </Alert>
          </div>
        </Section>

        {/* Vision */}
        <div className="mt-8 p-6 bg-slate-800 rounded-lg text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-600" />
          <h3 className="text-2xl font-bold text-white mb-3">
            ✨ {translations.guideVision}
          </h3>
          <p className="text-lg italic text-white mb-4">
            {translations.guideVisionQuote}
          </p>
          <p className="text-white">
            {translations.guideVisionText}
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-300">
          <p>{translations.guideFooter}</p>
        </div>
      </div>
    </div>
  );
};

export default Guide;
